import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ScanFace,
  Plus,
  Search,
  Edit,
  Trash2,
  RefreshCw,
  AlertCircle,
  Clock,
  DollarSign,
  Shield,
  ToggleLeft,
  ToggleRight,
  Globe,
  Building2,
  FileText,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button, Input, Modal, Select } from '../../components/ui';
import { formatCurrency } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';
import {
  useFacialVerificationRules,
  useCreateFacialVerificationRule,
  useUpdateFacialVerificationRule,
  useDeleteFacialVerificationRule,
  useEnableFacialVerificationRule,
  useDisableFacialVerificationRule,
  useFacialVerificationAuditLogs,
  FacialVerificationRule,
  FacialVerificationOperationType,
  OPERATION_TYPES,
  OPERATION_TYPE_LABELS,
  CONDITION_TYPES,
} from '../../hooks/useFacialVerification';

export function FacialVerificationRules() {
  const { user, isSuperAdmin } = useAuthStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [operationFilter, setOperationFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<FacialVerificationRule | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    operationType: 'PIX_SEND' as FacialVerificationOperationType,
    valueThreshold: '',
    timeWindowStart: '',
    timeWindowEnd: '',
    timezone: 'America/Sao_Paulo',
    conditionType: 'OR' as 'AND' | 'OR',
    priority: 0,
    tenantId: '',
  });

  // Fetch rules
  const { data: rules = [], isLoading, error, refetch } = useFacialVerificationRules({
    operationType: operationFilter as FacialVerificationOperationType || undefined,
    isEnabled: statusFilter === '' ? undefined : statusFilter === 'true',
    includeGlobal: true,
  });

  // Fetch audit logs
  const { data: auditData } = useFacialVerificationAuditLogs({ limit: 50 });

  // Mutations
  const createMutation = useCreateFacialVerificationRule();
  const updateMutation = useUpdateFacialVerificationRule();
  const deleteMutation = useDeleteFacialVerificationRule();
  const enableMutation = useEnableFacialVerificationRule();
  const disableMutation = useDisableFacialVerificationRule();

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      operationType: 'PIX_SEND',
      valueThreshold: '',
      timeWindowStart: '',
      timeWindowEnd: '',
      timezone: 'America/Sao_Paulo',
      conditionType: 'OR',
      priority: 0,
      tenantId: '',
    });
    setEditingRule(null);
  };

  const handleEdit = (rule: FacialVerificationRule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description || '',
      operationType: rule.operationType,
      valueThreshold: (Number(rule.valueThreshold) / 100).toString(),
      timeWindowStart: rule.timeWindowStart || '',
      timeWindowEnd: rule.timeWindowEnd || '',
      timezone: rule.timezone,
      conditionType: rule.conditionType,
      priority: rule.priority,
      tenantId: rule.tenantId || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (rule: FacialVerificationRule) => {
    if (confirm(`Deseja remover a regra "${rule.name}"?`)) {
      await deleteMutation.mutateAsync(rule.id);
    }
  };

  const handleToggle = async (rule: FacialVerificationRule) => {
    if (rule.isEnabled) {
      await disableMutation.mutateAsync(rule.id);
    } else {
      await enableMutation.mutateAsync(rule.id);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const payload = {
      name: formData.name,
      description: formData.description || undefined,
      operationType: formData.operationType,
      valueThreshold: String(Math.round(parseFloat(formData.valueThreshold) * 100)),
      timeWindowStart: formData.timeWindowStart || undefined,
      timeWindowEnd: formData.timeWindowEnd || undefined,
      timezone: formData.timezone,
      conditionType: formData.conditionType,
      priority: formData.priority,
      tenantId: isSuperAdmin() && formData.tenantId ? formData.tenantId : undefined,
    };

    if (editingRule) {
      await updateMutation.mutateAsync({ id: editingRule.id, data: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
    
    setIsModalOpen(false);
    resetForm();
  };

  // Filter rules
  const filteredRules = rules.filter(rule =>
    rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    OPERATION_TYPE_LABELS[rule.operationType].toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatThreshold = (value: string) => {
    return formatCurrency(Number(value));
  };

  const formatTimeWindow = (rule: FacialVerificationRule) => {
    if (!rule.timeWindowStart || !rule.timeWindowEnd) return null;
    return `${rule.timeWindowStart} - ${rule.timeWindowEnd}`;
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="w-16 h-16 text-red-400" />
        <h2 className="text-xl font-semibold text-surface-100">Erro ao carregar regras</h2>
        <Button variant="primary" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Verificação Facial</h1>
          <p className="text-surface-400">Configure regras de verificação facial para operações financeiras</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setIsAuditModalOpen(true)}>
            <FileText className="w-4 h-4 mr-2" />
            Logs de Auditoria
          </Button>
          <Button variant="primary" onClick={() => { resetForm(); setIsModalOpen(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Nova Regra
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <ScanFace className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-surface-100">{rules.length}</p>
              <p className="text-sm text-surface-400">Total de Regras</p>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-surface-100">
                {rules.filter(r => r.isEnabled).length}
              </p>
              <p className="text-sm text-surface-400">Regras Ativas</p>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Globe className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-surface-100">
                {rules.filter(r => !r.tenantId).length}
              </p>
              <p className="text-sm text-surface-400">Regras Globais</p>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-surface-100">
                {rules.filter(r => r.tenantId).length}
              </p>
              <p className="text-sm text-surface-400">Regras de Tenant</p>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardBody>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nome ou tipo de operação..."
                leftIcon={<Search className="w-4 h-4" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select
              value={operationFilter}
              onChange={(e) => setOperationFilter(e.target.value)}
              options={[
                { value: '', label: 'Todas as operações' },
                ...OPERATION_TYPES,
              ]}
              className="w-56"
            />
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: '', label: 'Todos os status' },
                { value: 'true', label: 'Ativas' },
                { value: 'false', label: 'Inativas' },
              ]}
              className="w-40"
            />
            <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Rules List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ScanFace className="w-5 h-5 text-primary-400" />
            Regras Configuradas
            <Badge variant="info">{filteredRules.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="animate-pulse bg-surface-800 rounded h-20" />
              ))}
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="text-center py-12 text-surface-400">
              <ScanFace className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma regra de verificação facial configurada</p>
            </div>
          ) : (
            <div className="divide-y divide-surface-800">
              {filteredRules.map(rule => (
                <RuleRow
                  key={rule.id}
                  rule={rule}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggle={handleToggle}
                  formatThreshold={formatThreshold}
                  formatTimeWindow={formatTimeWindow}
                  isToggling={enableMutation.isPending || disableMutation.isPending}
                />
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); resetForm(); }}
        title={editingRule ? 'Editar Regra' : 'Nova Regra de Verificação Facial'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nome da Regra"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ex: PIX acima de R$ 5.000"
            required
          />

          <Input
            label="Descrição (opcional)"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Descrição detalhada da regra"
          />

          <Select
            label="Tipo de Operação"
            value={formData.operationType}
            onChange={(e) => setFormData({ ...formData, operationType: e.target.value as FacialVerificationOperationType })}
            options={OPERATION_TYPES}
            required
            disabled={!!editingRule}
          />

          <Input
            label="Valor Limite (R$)"
            type="number"
            step="0.01"
            min="0"
            value={formData.valueThreshold}
            onChange={(e) => setFormData({ ...formData, valueThreshold: e.target.value })}
            placeholder="Verificação exigida acima deste valor"
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Horário Início (opcional)"
              type="time"
              value={formData.timeWindowStart}
              onChange={(e) => setFormData({ ...formData, timeWindowStart: e.target.value })}
              placeholder="HH:mm"
            />
            <Input
              label="Horário Fim (opcional)"
              type="time"
              value={formData.timeWindowEnd}
              onChange={(e) => setFormData({ ...formData, timeWindowEnd: e.target.value })}
              placeholder="HH:mm"
            />
          </div>

          {(formData.timeWindowStart || formData.timeWindowEnd) && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-sm text-blue-300">
              <p>A verificação será exigida para operações FORA da janela de horário configurada.</p>
            </div>
          )}

          <Select
            label="Condição"
            value={formData.conditionType}
            onChange={(e) => setFormData({ ...formData, conditionType: e.target.value as 'AND' | 'OR' })}
            options={CONDITION_TYPES}
          />

          <Input
            label="Prioridade"
            type="number"
            min="0"
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
            placeholder="Maior = avaliado primeiro"
          />

          {isSuperAdmin() && !editingRule && (
            <div className="p-3 bg-surface-800 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!formData.tenantId}
                  onChange={(e) => setFormData({ ...formData, tenantId: e.target.checked ? '' : user?.tenantId || '' })}
                  className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm">Regra Global (aplica a todos os tenants)</span>
              </label>
            </div>
          )}

          <div className="p-3 bg-surface-800 rounded-lg text-sm text-surface-400">
            <p className="font-medium text-surface-300 mb-1">Resumo da Regra:</p>
            <p>
              Verificação facial será exigida para <strong>{OPERATION_TYPE_LABELS[formData.operationType]}</strong>
              {formData.valueThreshold && (
                <> com valor acima de <strong>{formatCurrency(parseFloat(formData.valueThreshold) * 100)}</strong></>
              )}
              {formData.timeWindowStart && formData.timeWindowEnd && (
                <> {formData.conditionType === 'AND' ? 'E' : 'OU'} fora do horário <strong>{formData.timeWindowStart} - {formData.timeWindowEnd}</strong></>
              )}
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={createMutation.isPending || updateMutation.isPending}
            >
              {editingRule ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Audit Logs Modal */}
      <Modal
        isOpen={isAuditModalOpen}
        onClose={() => setIsAuditModalOpen(false)}
        title="Logs de Auditoria - Verificação Facial"
        size="xl"
      >
        <div className="max-h-[60vh] overflow-y-auto">
          {auditData?.logs && auditData.logs.length > 0 ? (
            <div className="divide-y divide-surface-800">
              {auditData.logs.map(log => (
                <div key={log.id} className="py-3 px-2">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={log.verificationRequired ? 'warning' : 'success'}>
                        {log.verificationRequired ? 'Verificação Exigida' : 'Sem Verificação'}
                      </Badge>
                      <span className="text-sm text-surface-300">
                        {OPERATION_TYPE_LABELS[log.operationType]}
                      </span>
                    </div>
                    <span className="text-xs text-surface-500">
                      {new Date(log.createdAt).toLocaleString('pt-BR')}
                    </span>
                  </div>
                  <div className="text-sm text-surface-400">
                    <span>Valor: {formatCurrency(Number(log.operationValue))}</span>
                    {log.user && <span className="ml-4">Usuário: {log.user.name}</span>}
                    {log.bypassTokenUsed && (
                      <Badge variant="info" className="ml-2">Bypass Token</Badge>
                    )}
                  </div>
                  {log.reason && (
                    <p className="text-xs text-surface-500 mt-1">{log.reason}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-surface-400">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum log de auditoria encontrado</p>
            </div>
          )}
        </div>
      </Modal>
    </motion.div>
  );
}

// Rule Row Component
interface RuleRowProps {
  rule: FacialVerificationRule;
  onEdit: (rule: FacialVerificationRule) => void;
  onDelete: (rule: FacialVerificationRule) => void;
  onToggle: (rule: FacialVerificationRule) => void;
  formatThreshold: (value: string) => string;
  formatTimeWindow: (rule: FacialVerificationRule) => string | null;
  isToggling: boolean;
}

function RuleRow({ rule, onEdit, onDelete, onToggle, formatThreshold, formatTimeWindow, isToggling }: RuleRowProps) {
  const timeWindow = formatTimeWindow(rule);
  
  return (
    <div className="flex items-center justify-between px-6 py-4 hover:bg-surface-800/30 transition-colors">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          rule.isEnabled ? 'bg-green-500/20' : 'bg-surface-700'
        }`}>
          <ScanFace className={`w-6 h-6 ${rule.isEnabled ? 'text-green-400' : 'text-surface-500'}`} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-surface-100">{rule.name}</p>
            {!rule.tenantId && (
              <Badge variant="info" className="text-xs">Global</Badge>
            )}
          </div>
          <p className="text-sm text-surface-500">
            {OPERATION_TYPE_LABELS[rule.operationType]}
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-6">
        {/* Conditions */}
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1 text-surface-400">
            <DollarSign className="w-4 h-4" />
            <span>&gt; {formatThreshold(rule.valueThreshold)}</span>
          </div>
          {timeWindow && (
            <>
              <span className="text-surface-600">{rule.conditionType}</span>
              <div className="flex items-center gap-1 text-surface-400">
                <Clock className="w-4 h-4" />
                <span>Fora de {timeWindow}</span>
              </div>
            </>
          )}
        </div>
        
        {/* Status Badge */}
        <Badge variant={rule.isEnabled ? 'success' : 'error'}>
          {rule.isEnabled ? 'Ativa' : 'Inativa'}
        </Badge>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onToggle(rule)}
            disabled={isToggling}
            title={rule.isEnabled ? 'Desativar' : 'Ativar'}
          >
            {rule.isEnabled ? (
              <ToggleRight className="w-5 h-5 text-green-400" />
            ) : (
              <ToggleLeft className="w-5 h-5 text-surface-500" />
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onEdit(rule)}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(rule)}>
            <Trash2 className="w-4 h-4 text-error" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default FacialVerificationRules;
